import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import React from 'react';

import BreadCrumbNav from '../components/Common/BreadCrumbNav';
import ContentContainer from '../components/Common/ContentContainer';
import IframeModal from '../components/Common/IframeModal';
import { ArrowLink } from '../components/Common/Links';
import PageSection from '../components/Common/PageSection';
import RichTextArticle from '../components/Common/RichText/RichTextArticle';
import Layout, { BACKGROUND } from '../components/Layouts/Layout';
import LearningCenterSeo from '../components/LearningCenter/LearningCenterSeo';
import * as styles from './resourcePost.module.css';

const LearningPost = ({ data, location }) => {
  const content = data.post;
  const url = location.href ? location.href : '';
  const ArticleContent = data.post.body;
  const hideHeroSection = content.hideHeroSection;
  const isSvg = get(content, 'heroImage.file.contentType') === 'image/svg+xml';
  const mainImage = get(content, 'heroImage.file.url');
  const { ogImage, ...rest } = get(content, 'pageMeta', {}) || {};
  const meta = {
    ogImageUrl: get(ogImage, 'resize.src') ? ogImage.resize.src : mainImage,
    ...rest,
  };
  return (
    <Layout background={BACKGROUND.GREY}>
      <LearningCenterSeo meta={meta} slug={location.pathname} />
      <div className={'publish-date'} data-post-date={content.publishDate} />
      <ContentContainer>
        <BreadCrumbNav
          linkText="Back to Resources"
          linkUrl="/resources/"
          shareUrl={url}
          shareTitle={get(content, 'title')}
          hideSocialIcons={content.hideSocialIcons}
        />

        {!hideHeroSection && (
          <div className={styles.hero}>
            <div className={styles.imgContainer}>
              {isSvg ? (
                <img src={mainImage} className={styles.heroImageSvg} alt={'Hero'} />
              ) : (
                <GatsbyImage
                  className={styles.heroImage}
                  image={get(content, 'heroImage.gatsbyImageData')}
                  alt={get(content, 'title', '')}
                />
              )}
              <div className={isSvg ? styles.bgSvg : styles.bg} />
            </div>
            <div className={styles.contentContainer}>
              <h1>{content.title}</h1>
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: get(content, 'articleDescription.childMarkdownRemark.html', '') }}
              />
              {get(content, 'downloadableContent.file.url') && (
                <ArrowLink
                  url={get(content, 'downloadableContent.file.url')}
                  label={'Download the paper'}
                  isArrowDown
                  isLarge
                  isExternal
                  isDownload
                />
              )}
            </div>
          </div>
        )}
      </ContentContainer>
      <PageSection isTopSection={content.hideHeroSection} className={hideHeroSection ? styles.pullUp : ''}>
        <RichTextArticle copy={ArticleContent} />
      </PageSection>
    </Layout>
  );
};

export const pageQuery = graphql`
  query GetLearningCenterPost($pageId: String!) {
    post: contentfulResourceCenterPost(contentful_id: { eq: $pageId }) {
      title
      type {
        typeName
      }
      publishDate
      hideSocialIcons
      hideHeroSection
      theme {
        themeName
      }
      pageMeta {
        ...BlogMeta
      }
      articleDescription {
        childMarkdownRemark {
          html
        }
      }
      ctaExternalLink
      downloadableContent {
        file {
          url
        }
      }
      heroImage {
        file {
          url
          contentType
        }
        fluid(maxWidth: 600, quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
        gatsbyImageData(formats: [AUTO, WEBP], layout: FULL_WIDTH, placeholder: BLURRED)
      }
      body {
        raw
        references {
          ... on ContentfulAsset {
            ...AssetEmbed
          }
          ... on ContentfulVideoEmbed {
            ...VideoEmbed
          }
          ... on ContentfulArticleImage {
            ...InlinePostImage
          }
          ... on Node {
            ... on ContentfulBlogSectionCalloutEmbed {
              ...CalloutEmbed
            }
          }
          ... on Node {
            ... on ContentfulJumpLink {
              ...JumpLink
            }
          }
          ... on Node {
            ... on ContentfulCallToAction {
              ...CallToAction
            }
          }
          ... on Node {
            ... on ContentfulLink {
              ...LinkEmbed
            }
          }
        }
      }
      contentType {
        viewLabel
      }
    }
  }
`;

LearningPost.propTypes = {
  data: PropTypes.object,
  location: PropTypes.any,
};

export default LearningPost;
